import React, { useState, useEffect, useContext } from "react"
import { NavLink, Link, useLocation } from "react-router-dom"
import { Home, Map, CreditCard, Menu, X, Info, Utensils, Trophy, Shield, LogOut, Mail, Receipt } from "lucide-react"

import { BookingContext } from "../../utils/providers/isolated-booking"

import styles from "./portal-mobile-nav.module.scss"

/**
 * Native-app-style bottom navigation for mobile only (hidden on desktop, where
 * the header pill nav takes over).
 *
 *   - A fixed bottom bar with the primary tabs (Home / Itinerary / Payments)
 *     plus a "More" button.
 *   - "More" opens a slide-up drawer with the secondary sections and a
 *     contact-your-agent card pinned at the bottom.
 */
export default function PortalMobileNav({ base }) {
	const { agent } = useContext(BookingContext)
	const location = useLocation()
	const [drawerOpen, setDrawerOpen] = useState(false)

	// Close the drawer whenever the route changes (e.g. tapping a drawer link).
	useEffect(() => {
		setDrawerOpen(false)
	}, [location.pathname])

	const tabs = [
		{ to: base, exact: true, label: "Home", Icon: Home },
		{ to: `${base}/itinerary`, label: "Itinerary", Icon: Map },
		{ to: `${base}/payments`, label: "Payments", Icon: CreditCard },
	]

	const moreLinks = [
		{ to: `${base}/pricing`, label: "Pricing breakdown", Icon: Receipt },
		// Messages — hidden from the drawer for now; route + logic kept intact.
		// { to: `${base}/messages`, label: "Message your agent", Icon: MessageSquare },
		{ to: `${base}/checkin`, label: "Check-in times", Icon: Info },
		{ to: `${base}/nearby`, label: "Nearby restaurants", Icon: Utensils },
		{ to: `${base}/stableford`, label: "Stableford points", Icon: Trophy },
		{ href: "https://www.golfinsurancedirect.com", label: "Book travel insurance", Icon: Shield, external: true },
	]

	// Highlight "More" when viewing one of its sections (or when the drawer is open).
	const onMoreRoute = moreLinks.some((item) => item.to && location.pathname.startsWith(item.to))

	const hasAgent = agent?.first_name || agent?.email
	const agentInitials = (agent?.first_name?.[0] || "") + (agent?.last_name?.[0] || "") || "GHD"
	const agentPhoto = agent?.photo
	const agentFullName = `${agent?.first_name || ""} ${agent?.last_name || ""}`.trim() || "Sales agent"

	return (
		<>
			{/* Drawer + backdrop */}
			{drawerOpen && (
				<div
					className={styles.backdrop}
					onClick={() => setDrawerOpen(false)}
				/>
			)}

			<div
				className={`${styles.drawer} ${drawerOpen ? styles.drawerOpen : ""}`}
				role="dialog"
				aria-modal="true"
				aria-hidden={!drawerOpen}>
				<div className={styles.handle} />

				<div className={styles.drawerHead}>
					<h3>More</h3>
					<button
						type="button"
						className={styles.close}
						onClick={() => setDrawerOpen(false)}
						aria-label="Close">
						<X size={20} />
					</button>
				</div>

				<nav className={styles.drawerLinks}>
					{moreLinks.map((item) =>
						item.external ? (
							<a
								key={item.label}
								href={item.href}
								target="_blank"
								rel="noreferrer"
								className={styles.drawerLink}>
								<item.Icon size={19} />
								<span>{item.label}</span>
							</a>
						) : (
							<NavLink
								key={item.to}
								to={item.to}
								className={styles.drawerLink}
								activeClassName={styles.drawerLinkActive}>
								<item.Icon size={19} />
								<span>{item.label}</span>
							</NavLink>
						),
					)}

					<Link
						to="/mybooking"
						className={styles.drawerLink}>
						<LogOut size={19} />
						<span>Exit booking</span>
					</Link>
				</nav>

				{hasAgent ? (
					<div className={styles.agentCard}>
						<div className={styles.agentAvatar}>
							{agentPhoto ? (
								<img
									src={agentPhoto}
									alt={agentFullName}
								/>
							) : (
								agentInitials
							)}
						</div>
						<div className={styles.agentText}>
							<small>Your personal sales agent</small>
							<strong>
								{agent.first_name} {agent.last_name}
							</strong>
						</div>
						{agent.email ? (
							<a
								href={`mailto:${agent.email}`}
								className={styles.agentBtn}>
								<Mail size={16} />
								<span>Email</span>
							</a>
						) : null}
					</div>
				) : null}
			</div>

			{/* Bottom bar */}
			<nav className={styles.bar}>
				{tabs.map(({ to, exact, label, Icon }) => (
					<NavLink
						key={to}
						to={to}
						exact={exact}
						className={styles.tab}
						activeClassName={styles.tabActive}>
						<Icon size={21} />
						<span>{label}</span>
					</NavLink>
				))}

				<button
					type="button"
					className={`${styles.tab} ${onMoreRoute || drawerOpen ? styles.tabActive : ""}`}
					onClick={() => setDrawerOpen((open) => !open)}>
					<Menu size={21} />
					<span>More</span>
				</button>
			</nav>
		</>
	)
}
